import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Lock, Satellite, LogOut } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientAccess() {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [existingSession, setExistingSession] = useState(null);
  const navigate = useNavigate();

  // Check for existing session on load
  useEffect(() => {
    const savedPassword = localStorage.getItem("clientPassword");
    const savedCategoryId = localStorage.getItem("categoryId");
    const savedCategoryName = localStorage.getItem("categoryName");
    
    if (savedPassword && savedCategoryId && savedCategoryName) {
      setExistingSession({ categoryId: savedCategoryId, categoryName: savedCategoryName });
    }
  }, []);

  const handleContinueSession = () => {
    const categoryId = localStorage.getItem("categoryId");
    navigate(`/project/${categoryId}`);
  };

  const handleLogout = () => {
    localStorage.removeItem("clientPassword");
    localStorage.removeItem("categoryId");
    localStorage.removeItem("categoryName");
    setExistingSession(null);
    toast.success("Sesión cerrada");
  };

  const handleAccess = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error("Ingresa una contraseña");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/client/access`, { password });
      const { category_id, category_name, reminders } = response.data;
      
      // Store in localStorage for persistence
      localStorage.setItem("clientPassword", password);
      localStorage.setItem("categoryId", category_id);
      localStorage.setItem("categoryName", category_name);
      localStorage.setItem("clientReminders", JSON.stringify(reminders || []));
      
      toast.success(`Acceso concedido: ${category_name}`);
      navigate(`/project/${category_id}`);
    } catch (error) {
      toast.error("Contraseña inválida");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-bg min-h-screen flex items-center justify-center p-4">
      <div className="login-overlay absolute inset-0" />
      
      <div 
        className="relative z-10 w-full max-w-md animate-fade-in"
        data-testid="client-access-container"
      >
        {/* Logo/Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-md bg-card border border-border mb-6">
            <Satellite className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-4xl font-bold uppercase tracking-widest text-foreground mb-2">
            ORBITAL ACCESS
          </h1>
          <p className="text-muted-foreground text-sm tracking-wide">
            SISTEMA DE RETROALIMENTACIÓN
          </p>
        </div>

        {/* Existing Session */}
        {existingSession && (
          <div className="bg-card border border-primary/50 rounded-md p-6 mb-6">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Sesión activa</p>
            <p className="font-semibold text-lg mb-4">{existingSession.categoryName}</p>
            <div className="flex gap-3">
              <Button
                onClick={handleContinueSession}
                className="flex-1 bg-primary hover:bg-primary/90 uppercase tracking-wider"
                data-testid="continue-session-button"
              >
                Continuar
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-border"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Access Form */}
        <div className="bg-card border border-border rounded-md p-8">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-4">
            {existingSession ? "O accede a otro proyecto" : "Ingresa tu código de acceso"}
          </p>
          <form onSubmit={handleAccess} className="space-y-6">
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                Código de Acceso
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ingresa tu contraseña"
                  className="pl-10 h-12 bg-input border-border font-mono text-lg tracking-wider"
                  data-testid="client-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground uppercase tracking-widest font-semibold btn-glow"
              data-testid="client-access-button"
            >
              {loading ? "VERIFICANDO..." : "INICIALIZAR SESIÓN"}
            </Button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-muted-foreground text-xs mt-8 tracking-wide">
          ORBITAL FEEDBACK SYSTEM v1.0
        </p>
      </div>
    </div>
  );
}
